import { ReactElement, ReactNode } from 'react';
import { Company } from 'types';
import { useHasPerms } from 'core/permissions';
import { permissions } from './permissions';
import { UserField, useDefaultOptions } from 'components';
import { TextField, DateRangePickerField, DecimalField } from '@ff-it/form';
import { CompanyField } from 'modules/core/company/components';
import { ClientBrandField } from 'modules/client/brand/components';
import { ClientPersonField } from 'modules/client/persons/components';

import { ClientAgreementField } from './ClientAgreementField';
import { ClientPONumberField } from './ClientPONumberField';
import { AttachmentField } from 'modules/attachments/AttachmentField';
import { RichTextField, type ReferenceMetadata } from 'components/RichText';
import { CampaignGoalsField } from 'modules/campaign/campaign/CampaignGoalsField';
import { Box, Stack } from '@ff-it/ui';

function FieldGroup({ title, children }: { title: ReactNode; children: ReactNode }): ReactElement {
  return (
    <Stack direction="vertical" space="0" mt="3">
      <Box
        as="h3"
        mb="3"
        textSize="xs"
        color="gray500"
        fontWeight="semibold"
        textTransform="uppercase"
        pb="0.5"
        borderBottom="subtle"
      >
        {title}
      </Box>
      {children}
    </Stack>
  );
}

export function Form({ references }: { references?: ReferenceMetadata }): ReactElement {
  const [canCreate, manage] = useHasPerms(permissions.add, 'campaign.manage_campaign');
  // Precedence for superuser on in case user has both perms
  const isManager = manage && !canCreate;
  const defaultClients = useDefaultOptions<Company>('client/clients/', { managed: true });
  return (
    <div className="container px-0 ml-0" style={{ maxWidth: '896px' }}>
      <FieldGroup title="Campaign setup">
        <TextField name="title" label="Title" required />
        <div className="row">
          <CompanyField
            name="client"
            label="Client"
            defaultOptions={defaultClients}
            required
            includeBranches={true}
            className="col-md-6"
          />
          <CompanyField
            name="payer"
            label="Payer"
            help="Leave empty to assign client"
            includeBranches={true}
            className="col-md-6"
          />
        </div>
      </FieldGroup>
      <FieldGroup title="Brief">
        <div className="row">
          <DateRangePickerField label="Period" fromName="date_from" toName="date_to" className="col-md-6" required />
          <ClientBrandField name="brand" label="Brand" className="col-md-6" />
        </div>
        <div className="row">
          <CampaignGoalsField name="goals" label="Goals" className="col-md-6" />
          <DecimalField name="total_budget" label="Total campaign budget" className="col-md-6" />
        </div>
        <RichTextField name="brief" label="Brief" references={references} />
      </FieldGroup>

      <FieldGroup title="Terms & agreement">
        <div className="row">
          <ClientAgreementField name="agreement" label="Agreement" className="col-md-4" />
          <ClientPONumberField
            name="client_po_number"
            label="PO number"
            help="PO number of client"
            className="col-md-4"
          />
          <ClientPersonField name="ordered_by" label="Person" help="Ordered by" className="col-md-4" />
        </div>
      </FieldGroup>

      <FieldGroup title="Team">
        <div className="row">
          <UserField
            name="manager"
            label="Manager"
            className="col-md-6"
            required
            disabled={isManager}
            filter={{ manage_campaign: true }}
          />
          <UserField
            name="involved"
            label="Involved managers"
            className="col-md-6"
            filter={{ manage_campaign: true }}
            isMulti
          />
        </div>
      </FieldGroup>

      <FieldGroup title="Additional">
        <div className="row">
          <TextField name="comment" label="Comment" multiline className="col-md-6" />
          <AttachmentField name="attachments" label="Attachments" className="col-md-6" />
        </div>
      </FieldGroup>
    </div>
  );
}
